import { useState, useRef, useEffect } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface OverlayPosition {
  x: number; // percentage 0-100
  y: number; // percentage 0-100
  width: number; // percentage 0-100
  height: number; // percentage 0-100
}

interface LocationPhotoEditorProps {
  photoUrl?: string;
  overlayPosition?: OverlayPosition;
  onSave: (file: File, overlayPosition: OverlayPosition) => Promise<void>;
  onRemove?: () => Promise<void>;
}

export function LocationPhotoEditor({
  photoUrl,
  overlayPosition: initialOverlay,
  onSave,
  onRemove,
}: LocationPhotoEditorProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(photoUrl || '');
  const [overlayPosition, setOverlayPosition] = useState<OverlayPosition>(
    initialOverlay || { x: 40, y: 40, width: 20, height: 15 }
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isResizingWidth, setIsResizingWidth] = useState(false);
  const [isResizingHeight, setIsResizingHeight] = useState(false);
  const [isResizingCorner, setIsResizingCorner] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (photoUrl) {
      setPreviewUrl(photoUrl);
    }
  }, [photoUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleMouseDown = (e: React.MouseEvent, resizeType: 'none' | 'width' | 'height' | 'corner') => {
    e.preventDefault();
    if (resizeType === 'width') {
      setIsResizingWidth(true);
    } else if (resizeType === 'height') {
      setIsResizingHeight(true);
    } else if (resizeType === 'corner') {
      setIsResizingCorner(true);
    } else {
      setIsDragging(true);
    }
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    if (!isDragging && !isResizingWidth && !isResizingHeight && !isResizingCorner) return;

    const rect = containerRef.current.getBoundingClientRect();
    const deltaX = ((e.clientX - dragStart.x) / rect.width) * 100;
    const deltaY = ((e.clientY - dragStart.y) / rect.height) * 100;

    if (isResizingCorner) {
      // Resize both width and height
      setOverlayPosition((prev) => ({
        ...prev,
        width: Math.max(5, Math.min(100 - prev.x, prev.width + deltaX)),
        height: Math.max(5, Math.min(100 - prev.y, prev.height + deltaY)),
      }));
    } else if (isResizingWidth) {
      // Resize only width
      setOverlayPosition((prev) => ({
        ...prev,
        width: Math.max(5, Math.min(100 - prev.x, prev.width + deltaX)),
      }));
    } else if (isResizingHeight) {
      // Resize only height
      setOverlayPosition((prev) => ({
        ...prev,
        height: Math.max(5, Math.min(100 - prev.y, prev.height + deltaY)),
      }));
    } else if (isDragging) {
      // Move the rectangle
      setOverlayPosition((prev) => ({
        ...prev,
        x: Math.max(0, Math.min(100 - prev.width, prev.x + deltaX)),
        y: Math.max(0, Math.min(100 - prev.height, prev.y + deltaY)),
      }));
    }

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizingWidth(false);
    setIsResizingHeight(false);
    setIsResizingCorner(false);
  };

  const handleSave = async () => {
    if (!selectedFile && !photoUrl) {
      alert('Please select a photo first');
      return;
    }

    setSaving(true);
    try {
      if (selectedFile) {
        await onSave(selectedFile, overlayPosition);
      }
      alert('Location photo saved successfully!');
    } catch (error) {
      console.error('Failed to save photo:', error);
      alert('Failed to save photo');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!onRemove) return;

    if (!confirm('Are you sure you want to remove this location photo?')) return;

    setRemoving(true);
    try {
      await onRemove();
      setPreviewUrl('');
      setSelectedFile(null);
      setOverlayPosition({ x: 40, y: 40, width: 20, height: 15 });
    } catch (error) {
      console.error('Failed to remove photo:', error);
      alert('Failed to remove photo');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Section */}
      {!previewUrl && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-primary w-full flex items-center justify-center"
          >
            <PhotoIcon className="w-5 h-5 mr-2" />
            Upload Location Photo
          </button>
          <p className="text-xs text-gray-500 mt-2">
            Upload a photo and drag the yellow square to mark the FLOC location
          </p>
        </div>
      )}

      {/* Preview with Overlay */}
      {previewUrl && (
        <div className="space-y-3">
          <div
            ref={containerRef}
            className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-100"
            style={{ aspectRatio: '16/9' }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              src={previewUrl}
              alt="Location"
              className="w-full h-full object-contain"
              draggable={false}
            />

            {/* Yellow Rectangle Overlay */}
            <div
              className="absolute border-4 border-yellow-400 bg-yellow-400 bg-opacity-30 cursor-move"
              style={{
                left: `${overlayPosition.x}%`,
                top: `${overlayPosition.y}%`,
                width: `${overlayPosition.width}%`,
                height: `${overlayPosition.height}%`,
              }}
              onMouseDown={(e) => handleMouseDown(e, 'none')}
            >
              {/* Right edge resize handle */}
              <div
                className="absolute top-0 right-0 w-2 h-full bg-yellow-500 cursor-ew-resize hover:bg-yellow-600"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleMouseDown(e, 'width');
                }}
              />
              {/* Bottom edge resize handle */}
              <div
                className="absolute bottom-0 left-0 w-full h-2 bg-yellow-500 cursor-ns-resize hover:bg-yellow-600"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleMouseDown(e, 'height');
                }}
              />
              {/* Corner resize handle */}
              <div
                className="absolute bottom-0 right-0 w-4 h-4 bg-yellow-500 cursor-se-resize hover:bg-yellow-600 z-10"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleMouseDown(e, 'corner');
                }}
              />
            </div>
          </div>

          <p className="text-sm text-gray-600">
            Drag the yellow rectangle to mark the FLOC location. Drag edges or corner to resize.
          </p>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-secondary text-sm"
              disabled={saving || removing}
            >
              Change Photo
            </button>
            {selectedFile && (
              <button
                onClick={handleSave}
                disabled={saving || removing}
                className="btn btn-primary text-sm"
              >
                {saving ? 'Saving...' : 'Save Photo'}
              </button>
            )}
            {photoUrl && onRemove && (
              <button
                onClick={handleRemove}
                disabled={saving || removing}
                className="btn bg-red-600 hover:bg-red-700 text-white text-sm flex items-center ml-auto"
              >
                <XMarkIcon className="w-4 h-4 mr-1" />
                {removing ? 'Removing...' : 'Remove Photo'}
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
}
